import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "../axios/axiosInstance";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import { Skeleton } from "../components/ui/Skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/Alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { MapPin, Briefcase, AlertCircle, Building } from "lucide-react";

export default function DetailAnnonce() {
  const { id } = useParams();
  const [annonce, setAnnonce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError("ID de l'annonce manquant ou invalide.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    axios
      .get(`annonces/${id}`)
      .then((res) => {
        setAnnonce(res.data);
        console.log(annonce);
      })
      .catch((err) => {
        console.error(
          "Erreur lors de la récupération des détails de l'annonce :",
          err,
        );
        setError(
          err.response?.data?.detail ||
            err.message ||
            "Une erreur est survenue",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-12">
          <Skeleton className="h-12 w-3/4 mb-6" />
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-24 w-full mb-6" />
          <Skeleton className="h-8 w-1/3 mb-4" />
          <Skeleton className="h-20 w-full mb-6" />
          <Skeleton className="h-10 w-32 mt-8" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-12 flex flex-col items-center justify-center">
          <Alert variant="destructive" className="w-full max-w-lg">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Erreur</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" asChild className="mt-6">
            <Link to="/offres-emploi">Retour aux offres</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  if (!annonce) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-12 text-center">
          <p>Aucune donnée d'annonce disponible.</p>
          <Button variant="outline" asChild className="mt-6">
            <Link to="/offres-emploi">Retour aux offres</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto shadow-lg border border-gray-200 rounded-lg">
            <CardHeader className="bg-gray-100 p-6 rounded-t-lg">
              <CardTitle className="text-3xl md:text-4xl font-bold text-blue-700">
                {annonce.title}
              </CardTitle>
              {annonce.companyName && (
                <CardDescription className="text-lg text-gray-600 flex items-center pt-2">
                  <Building className="h-5 w-5 mr-2 text-blue-600" />
                  {annonce.companyName}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-8 p-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                  <Briefcase className="h-6 w-6 mr-3 text-blue-600" />
                  Profil recherché :
                </h3>
                <p className="text-gray-700 ml-9 whitespace-pre-line">
                  {annonce.profilRecherche}
                </p>
              </div>

              {annonce.description && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                    <Briefcase className="h-6 w-6 mr-3 text-blue-600" />
                    Description du poste :
                  </h3>
                  <p className="text-gray-700 ml-9 whitespace-pre-line">
                    {annonce.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 pt-4 border-t border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                    Lieu :
                  </h3>
                  <p className="text-gray-700 ml-7">{annonce.town}</p>
                </div>
                {annonce.contractType && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                      <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
                      Type de contrat :
                    </h3>
                    <p className="text-gray-700 ml-7">{annonce.contractType}</p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-center p-6 bg-gray-100 rounded-b-lg">
              <br></br>
              <Button
                size="lg"
                asChild
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-3 rounded-md shadow-md transition-transform transform hover:scale-105"
              >
                <Link to={`/postuler/${annonce._id}`}>
                  Postuler à cette offre
                </Link>
              </Button>
              <Button
                variant="outline"
                asChild
                className="mt-4 w-full md:w-auto"
              >
                <Link to="/offres-emploi">Retour à la liste des offres</Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
//Garder les filtres en mémoire
